import type {
  CommonProductFragment,
  CommonProductDetailFragment
} from '~/lib/Shop/generated/schema';

/**
 * Processes product API responses to replace configurable products with their specific variants
 * when a variant's SKU matches one of the requested SKUs.
 *
 * @param products - Array of products from the API response
 * @param requestedSkus - Array of SKUs that were requested
 * @returns Array of products with variants replaced where appropriate
 */
export function processProductVariants(
  products: (CommonProductFragment | CommonProductDetailFragment)[],
  requestedSkus: string[]
): (CommonProductFragment | CommonProductDetailFragment)[] {
  if (!products || !requestedSkus || requestedSkus.length === 0) {
    return products;
  }

  return products.map((product) => {
    // Check if this is a ConfigurableProduct with variants
    if (
      product.__typename === 'ConfigurableProduct' &&
      'variants' in product &&
      product.variants &&
      Array.isArray(product.variants)
    ) {
      // Look for a variant that matches one of our requested SKUs
      for (const variant of product.variants) {
        if (
          variant?.product?.sku &&
          requestedSkus.includes(variant.product.sku)
        ) {
          // Found a matching variant - return the variant product instead of the parent
          // We need to preserve the price_range from the parent if the variant doesn't have it
          const variantProduct = variant.product;

          // Create a new product object that combines the variant with necessary parent data
          return {
            ...variantProduct,
            // Ensure we have price information - use parent's price if variant doesn't have it
            price_range: variantProduct.price_range || product.price_range,
            // Preserve other important fields from parent if needed
            __typename: variantProduct.__typename || 'SimpleProduct'
          } as CommonProductFragment | CommonProductDetailFragment;
        }
      }
    }

    // No matching variant found or not a configurable product - return original
    return product;
  });
}
